import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GRID_PAGE_SIZE_OPTIONS } from "@/hooks/useGridPagination";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

export function GridPerPageSelect({
  id,
  pageSize,
  onPageSizeChange,
  options = GRID_PAGE_SIZE_OPTIONS,
}: {
  id: string;
  pageSize: number;
  onPageSizeChange: (n: number) => void;
  options?: readonly number[];
}) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <Label htmlFor={id} className="text-xs text-muted-foreground whitespace-nowrap">
        Per page
      </Label>
      <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
        <SelectTrigger id={id} className="glass-input h-10 w-[4.5rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((n) => (
            <SelectItem key={n} value={String(n)}>
              {n}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function GridPaginationFooter({
  totalCount,
  rangeStart,
  rangeEnd,
  page,
  totalPages,
  onPageChange,
}: {
  totalCount: number;
  rangeStart: number;
  rangeEnd: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalCount <= 0) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-1 py-2">
      <p className="text-sm text-muted-foreground">
        Showing <span className="font-medium text-foreground">{rangeStart}</span>
        {" – "}
        <span className="font-medium text-foreground">{rangeEnd}</span>
        {" of "}
        <span className="font-medium text-foreground">{totalCount}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="glass"
          size="sm"
          className="h-9 gap-1"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground tabular-nums px-2">
          Page {Math.min(page, totalPages)} of {totalPages}
        </span>
        <Button
          type="button"
          variant="glass"
          size="sm"
          className="h-9 gap-1"
          disabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/** Cursor-based catalog footer (no total count; 20 rows per API request). */
export function CatalogCursorFooter({
  rowCount,
  perPage,
  hasPrevious,
  hasMore,
  busy,
  initialLoading,
  onPrevious,
  onNext,
}: {
  rowCount: number;
  perPage: number;
  hasPrevious: boolean;
  hasMore: boolean;
  busy: boolean;
  initialLoading: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const disabled = busy || initialLoading;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-1 py-2">
      <p className="text-sm text-muted-foreground inline-flex flex-wrap items-center gap-x-1 gap-y-1">
        {initialLoading && rowCount === 0 ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
            Loading products…
          </>
        ) : rowCount === 0 ? (
          "No rows on this page."
        ) : (
          <>
            Showing <span className="font-medium text-foreground">{rowCount}</span>
            {perPage > 0 ? (
              <>
                {" "}
                (up to <span className="font-medium text-foreground">{perPage}</span> per request)
              </>
            ) : null}
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin shrink-0 ml-1" aria-hidden />
                <span className="ml-1">Updating…</span>
              </>
            ) : null}
          </>
        )}
      </p>
      <div className="flex items-center gap-2">
        <Button type="button" variant="glass" size="sm" className="h-9 gap-1" disabled={!hasPrevious || disabled} onClick={onPrevious}>
          <ChevronLeft className="w-4 h-4" />
          Previous
        </Button>
        <Button type="button" variant="glass" size="sm" className="h-9 gap-1" disabled={!hasMore || disabled} onClick={onNext}>
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
